<script setup>
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import Dropdown from "primevue/dropdown";
import {ref} from "vue";

const emits = defineEmits(["next"]);
const props = defineProps([
    "form",
    'errors',
    'rateFitnessOptions',
    'physicalOptions',
    'disciplineOptions',
    'drinkSoftdrinkOptions',
    'drinkCoffeeOptions',
    'drinkTeaOptions',
    'drinkAlcoholOptions',
    'v$',
]);

const metabolics = ref(["gut", "träge", "Abnehmblockade"]);
const optimizeBody = ref([
    "den Bauch straffen",
    "das Gesäss straffen",
    "die Oberschenkel straffen",
    "die Muskulatur aufbauen",
    "das Hautbild optimieren",
]);
const weightLossBlock = ref([
    "ja",
    "nein",
    "unbekannt"
])
const yesNo = ref([
    {
        label: "ja",
        value: true,
    },
    {
        label: "nein",
        value: false,
    }
]);
const working = ref(["sitzend", "stehend", "in Bewegung", "abwechselnd"]);
const perLiter = ref([
    "0.5 l",
    "1 l",
    "2 l",
    "3 l",
    "mehr",
]);
</script>

<template>
    <div class="lato p-7 rounded my-2">
        <div class="text-xl font-semibold mb-7">
            Erzähle uns von deinem Körper und deinen Gewohnheiten
        </div>

        <div class="my-7">
            <label class="p-1 font-bold"
                >Wie schätzt du deine Stoffwechselfunktion in Bezug auf deine
                Verbrennung ein?</label
            >

            <Dropdown
                :invalid="v$.form.metabolic_function.$error"
                v-model="form.metabolic_function"
                :options="metabolics"
                class="w-full md:w-14rem"
                @change="v$.form.metabolic_function.$touch"
            />


            <div v-if="v$.form.metabolic_function.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.metabolic_function.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold"
                >Liegt bei dir eine Abnehmblockade vor?</label
            >

            <Dropdown
                :invalid="v$.form.weight_loss_block.$error"
                v-model="form.weight_loss_block"
                :options="weightLossBlock"
                class="w-full md:w-14rem"
                @change="v$.form.weight_loss_block.$touch"
            />

            <div v-if="v$.form.weight_loss_block.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.weight_loss_block.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold">Wie ist dein Sättigungsgefühl?</label>

            <Dropdown
                :invalid="v$.form.satiety.$error"
                v-model="form.satiety"
                :options="['gut', 'eher träge']"
                class="w-full md:w-14rem"
                @change="v$.form.satiety.$touch"
            />

            <div v-if="v$.form.satiety.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.satiety.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <div class="font-bold">
                Um deine Körperform zu optimieren, möchtest du:
            </div>

            <div class="grid grid-cols-2 gap-4 my-7">
                <div class="" v-for="(item, i) in optimizeBody" :key="i">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            v-model="form.optimize_body_shape"
                            :value="item"
                        />

                        <div class="">{{ item }}</div>
                    </div>
                </div>

                <div class>
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :binary="true"
                            v-model="form.is_reduce_cellulite"
                        />

                        <div class="">Cellulite reduzieren</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold">Arbeitest du</label>

            <Dropdown
                :invalid="v$.form.do_you_work.$error"
                v-model="form.do_you_work"
                :options="working"
                class="w-full md:w-14rem"
                @change="v$.form.do_you_work.$touch"
            />

            <div v-if="v$.form.do_you_work.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.do_you_work.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <div class="font-bold">Machst du Sport</div>

            <div class="grid grid-cols-3 gap-4 mt-4">
                <div class="" v-for="(item, i) in yesNo" :key="i">
                    <div class="flex items-center gap-4">
                        <RadioButton v-model="form.do_sports"
                                     :invalid="v$.form.do_sports.$error"
                                     @change="v$.form.do_sports.$touch"
                                     :value="item.value" />

                        <div class="">{{ item.label }}</div>
                    </div>
                </div>
            </div>

            <div v-if="v$.form.do_sports.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.do_sports.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold">Welche Sportart übst du aus?</label>

            <InputText
                :invalid="v$.form.what_sport.$error"
                v-model="form.what_sport"
                type="text"
                class="w-full"
                @input="v$.form.what_sport.$touch"
                @blur="v$.form.what_sport.$touch" />

            <div v-if="v$.form.what_sport.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.what_sport.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold">Wie oft pro Woche treibst du Sport?</label>

            <Dropdown
                :invalid="v$.form.sports_times_per_week.$error"
                v-model="form.sports_times_per_week"
                :options="['1x', '2x', '3x', 'mehr']"
                class="w-full md:w-14rem"
                @change="v$.form.what_sport.$touch"
            />

            <div v-if="v$.form.sports_times_per_week.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.sports_times_per_week.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold">Wie lange pro Woche treibst du Sport?</label>

            <Dropdown
                :invalid="v$.form.long_exercise.$error"
                v-model="form.long_exercise"
                :options="['30 Min.', '1 Std.', '2 Std.', 'mehr']"
                class="w-full md:w-14rem"
                @change="v$.form.long_exercise.$touch"
            />

            <div v-if="v$.form.long_exercise.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.long_exercise.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold">Wie schätzt du deine Fitness ein?</label>

            <Dropdown
                v-model="form.rate_fitness"
                class="w-full"
                :options="rateFitnessOptions"
                optionLabel="label"
                optionValue="value"
            ></Dropdown>
        </div>

        <div class="my-7">
            <label class="p-1 font-bold">Wie schätzt du dich ein?</label>

            <div class="p-1 mt-5">Körperlich empfindlich oder robust</div>

            <Dropdown
                :invalid="v$.form.physical.$error"
                v-model="form.physical"
                class="w-full"
                :options="physicalOptions"
                optionLabel="label"
                optionValue="value"
                @change="v$.form.physical.$touch"
            ></Dropdown>

            <div v-if="v$.form.physical.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.physical.$errors[0].$message }}
            </div>

            <div class="p-1 mt-5">In Bezug auf deine Disziplin</div>

            <Dropdown
                :invalid="v$.form.discipline.$error"
                v-model="form.discipline"
                class="w-full"
                :options="disciplineOptions"
                optionLabel="label"
                optionValue="value"
                @change="v$.form.discipline.$touch"
            ></Dropdown>

            <div v-if="v$.form.discipline.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.discipline.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <div class="font-bold">Was möchtest du verbessern?</div>

            <div class="grid grid-cols-2 gap-4 my-4">
                <div
                    class=""
                    v-for="(item, i) in ['Ausdauer', 'Muskelkraft']"
                    :key="i"
                >
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :value="item"
                            v-model="form.want_to_improve"
                        />
                        <div class="">
                            {{ item }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="font-bold">Bist du Raucher?</div>

            <div class="grid grid-cols-2 gap-4 my-4">
                <div class="" v-for="(item, i) in yesNo" :key="i">
                    <div class="flex items-center gap-4">
                        <RadioButton
                            :invalid="v$.form.smoker.$error"
                            :value="item.value"
                            v-model="form.smoker"
                            @change="v$.form.smoker.$touch" />

                        <div class="">
                            {{ item.label }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="v$.form.smoker.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.smoker.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <div class="font-bold">Wieviel trinkst du am Tag?</div>
            <div class="grid grid-cols-2 gap-4 my-4">
                <div class="">
                    <label class="p-1 font-bold text-[14.5px]">{{ $t("Water") }}</label>

                    <Dropdown
                        :invalid="v$.form.drink_water.$error"
                        :options="perLiter"
                        v-model="form.drink_water"
                        class="w-full md:w-14rem"
                        @change="v$.form.drink_water.$touch"
                    >
                    </Dropdown>

                    <div v-if="v$.form.drink_water.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.drink_water.$errors[0].$message }}
                    </div>
                </div>

                <div class="">
                    <label class="p-1 text-[14.5px] font-bold">Süssgetränke</label>

                    <Dropdown
                        :invalid="v$.form.drink_softdrink.$error"
                        :options="drinkSoftdrinkOptions"
                        v-model="form.drink_softdrink"
                        class="w-full md:w-14rem"
                        @change="v$.form.drink_softdrink.$touch"
                    />

                    <div v-if="v$.form.drink_softdrink.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.drink_softdrink.$errors[0].$message }}
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-4 my-4">
                <div class="">
                    <label class="p-1 font-bold text-[14.5px]">Kaffee</label>

                    <Dropdown
                        :invalid="v$.form.drink_coffee.$error"
                        :options="drinkCoffeeOptions"
                        v-model="form.drink_coffee"
                        class="w-full md:w-14rem"
                        @change="v$.form.drink_coffee.$touch"
                    />

                    <div v-if="v$.form.drink_coffee.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.drink_coffee.$errors[0].$message }}
                    </div>
                </div>

                <div class="">
                    <label class="p-1 text-[14.5px] font-bold">Tee</label>

                    <Dropdown
                        :invalid="v$.form.drink_tea.$error"
                        :options="drinkTeaOptions"
                        v-model="form.drink_tea"
                        class="w-full md:w-14rem"
                        @change="v$.form.drink_tea.$touch"
                    />

                    <div v-if="v$.form.drink_tea.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.drink_tea.$errors[0].$message }}
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-4 my-4">
                <div class="">
                    <label class="p-1 text-[14.5px] font-bold">Alkohol</label>

                    <Dropdown
                        :invalid="v$.form.drink_alcohol.$error"
                        :options="drinkAlcoholOptions"
                        v-model="form.drink_alcohol"
                        class="w-full md:w-14rem"
                        @change="v$.form.drink_alcohol.$touch"
                    />

                    <div v-if="v$.form.drink_alcohol.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.drink_alcohol.$errors[0].$message }}
                    </div>
                </div>
            </div>
        </div>

        <div class="flex mt-10 justify-end">
            <Button
                label="weiter"
                class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                @click="emits('next', 'symptoms-and-diseases', 'anamnese')"
            />
        </div>
    </div>
</template>
